import * as React from 'react';
import { getStore } from '../store/store';
import { AttachmentPillData, AttachmentPillViewStatus } from '../store/schema/AttachmentPillData';
import RecentAttachmentPill from './RecentAttachmentPill';
import { observer } from 'mobx-react-lite';
import { DefaultButton, IButtonProps, IconButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { addAllRecentAttachmentsText } from './RecentAttachmentPills.locstring.json';
import { RecentAttachmentsPillsData } from '../store/schema/RecentAttachmentsPillsData';
import { lazySubscribeToResizeEvent, lazyUnsubscribeFromResizeEvent } from 'owa-resize-event';
import loc from 'owa-localize';
import {
    setIsExpanded,
    onCloseRecentAttachmentPills,
    onOverflowOneRow,
} from '../actions/internalActions';
import getAddNewAttachmentString from '../utils/getAddNewAttachmentString';
import { Icon } from '@fluentui/react/lib/Icon';
import { InfoBarHostViewState } from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import ItemId from 'owa-service/lib/contract/ItemId';
import { AttachmentWellViewState } from 'owa-attachment-well-data';
import { MailboxInfo } from 'owa-client-ids';
import createAttachmentFromRecentAttachmentsPills from '../utils/createAttachmentFromRecentAttachmentsPills';
import {
    DP_RECENT_ATTACHMENT_PILLS_FORCE_CLOSE,
    DP_RECENT_ATTACHMENT_PILLS_EXPAND,
    DP_RECENT_ATTACHMENT_PILLS_ADD_ALL,
    DP_RECENT_ATTACHMENT_PILLS_GENERAL_INTERACTION,
    ON_ADD_ALL,
    ON_EXPAND,
} from '../constants';
import { logUsage } from 'owa-analytics';

const classNames = require('classnames');
const styles = require('./RecentAttachmentPill.scss');

export interface RecentAttachmentPillContainerProps {
    composeId: string;
    itemId: ItemId;
    attachmentWell: AttachmentWellViewState;
    infoBar: InfoBarHostViewState;
    mailboxInfo: MailboxInfo;
}

const RecentAttachmentPillContainer = observer(function RecentAttachmentPillContainer(
    props: RecentAttachmentPillContainerProps
) {
    const [hasLoggedGeneralInteraction, setHasLoggedGeneralInteraction] = React.useState(false);
    React.useEffect(() => {
        lazySubscribeToResizeEvent.importAndExecute(internalCheckWhetherViewOverflowOneRow);
        return () => {
            lazyUnsubscribeFromResizeEvent.importAndExecute(internalCheckWhetherViewOverflowOneRow);
        };
    }, []);
    React.useEffect(() => {
        internalCheckWhetherViewOverflowOneRow();
    });

    const onClose = React.useCallback(() => {
        logUsage(DP_RECENT_ATTACHMENT_PILLS_FORCE_CLOSE);
        onCloseRecentAttachmentPills(props.composeId);
    }, [props.composeId]);

    const recentAttachmentPillsDiv = React.useRef<HTMLDivElement>();
    const internalCheckWhetherViewOverflowOneRow = () => {
        if (isElementInfoReady(recentAttachmentPillsDiv.current)) {
            const hasOverflowed = didViewOverflowOneRow(recentAttachmentPillsDiv.current);
            onOverflowOneRow(props.composeId, hasOverflowed);
        }
    };
    const pillsViewState = getStore().recentAttachmentPills.get(props.composeId);

    if (pillsViewState?.displayPills) {
        const { numberOfRecipients, recipientName } = pillsViewState;
        const logGeneralInteraction = (source: string) => {
            if (!hasLoggedGeneralInteraction) {
                logUsage(DP_RECENT_ATTACHMENT_PILLS_GENERAL_INTERACTION, {
                    source: source,
                });
                setHasLoggedGeneralInteraction(true);
            }
        };

        const onChevronClicked = () => {
            // log only when expanding
            if (!pillsViewState.isExpanded) {
                logUsage(DP_RECENT_ATTACHMENT_PILLS_EXPAND);
                logGeneralInteraction(ON_EXPAND);
            }

            setIsExpanded(props.composeId, !pillsViewState.isExpanded);
        };

        const {
            pills,
            pillContainerStyle,
            buttonStyle,
            buttonProps,
            mainContainerStyle,
        } = getDisplayProperties(props, pillsViewState, logGeneralInteraction, onChevronClicked);
        const getRecentAttachmentPillsDiv = (c: HTMLDivElement) => {
            recentAttachmentPillsDiv.current = c;
        };

        if (pills.length > 0) {
            return (
                <div className={classNames(styles.mainContainer, mainContainerStyle)}>
                    <div className={styles.topRecentAttachmentContainer}>
                        <span className={styles.addAttachmentString}>
                            {getAddNewAttachmentString(
                                numberOfRecipients,
                                pills.length,
                                recipientName
                            )}
                        </span>
                        <IconButton
                            className={styles.recentAttachmentIcon}
                            iconProps={{
                                iconName: ControlIcons.ChromeClose,
                                styles: {
                                    root: styles.closeIcon,
                                },
                            }}
                            onClick={onClose}
                        />
                    </div>
                    <div className={styles.bottomRecentAttachmentContainer}>
                        <div
                            className={classNames(styles.pillContainer, pillContainerStyle)}
                            ref={getRecentAttachmentPillsDiv}>
                            {getAddAllButton(
                                pills.length > 1,
                                props,
                                pillsViewState,
                                logGeneralInteraction
                            )}
                            {pills}
                        </div>
                        <IconButton
                            className={buttonStyle}
                            aria-expanded={pillsViewState.isExpanded}
                            disabled={buttonProps.disabled}
                            onClick={buttonProps.onClick}
                            iconProps={{
                                iconName: buttonProps.iconProps?.iconName,
                            }}></IconButton>
                    </div>
                </div>
            );
        }
    }
    return null;
});

function getAllPills(
    props: RecentAttachmentPillContainerProps,
    pillsViewState: RecentAttachmentsPillsData,
    logGeneralInteraction: (source: string) => void
): JSX.Element[] {
    const attachmentsPillsData: AttachmentPillData[] = pillsViewState.attachmentsPillData;
    let pills: JSX.Element[] = [];
    attachmentsPillsData.forEach(pillsData => {
        if (pillsData.attachmentPillViewStatus === AttachmentPillViewStatus.Show) {
            pills.push(
                <RecentAttachmentPill
                    attachmentPillsData={pillsData}
                    composeId={props.composeId}
                    key={pillsData.attachmentId?.Id}
                    itemId={props.itemId}
                    attachmentWell={props.attachmentWell}
                    infoBar={props.infoBar}
                    mailboxInfo={props.mailboxInfo}
                    logGeneralInteraction={logGeneralInteraction}
                />
            );
        }
    });
    return pills;
}

function isElementInfoReady(c: HTMLDivElement | undefined): boolean {
    return !!c && c.children.length > 0;
}

function didViewOverflowOneRow(c: HTMLDivElement | undefined): boolean {
    return !!c && c.children.length > 0 && c.scrollHeight > 2 * c.children[0].clientHeight;
}

function getAddAllButton(
    shouldShowButton: boolean,
    props: RecentAttachmentPillContainerProps,
    pillsViewState: RecentAttachmentsPillsData,
    logGeneralInteraction: (source: string) => void
) {
    const { itemId, attachmentWell, infoBar, mailboxInfo } = props;
    if (shouldShowButton) {
        const onAttachmentCreation = (attachmentCreated: boolean) => {
            //  pending on design
        };

        const onAddAllClicked = () => {
            createAttachmentFromRecentAttachmentsPills(
                pillsViewState.attachmentsPillData.filter(
                    pill => pill.attachmentPillViewStatus === AttachmentPillViewStatus.Show
                ),
                onAttachmentCreation,
                itemId,
                attachmentWell,
                infoBar,
                mailboxInfo
            );
            logUsage(DP_RECENT_ATTACHMENT_PILLS_ADD_ALL);
            logGeneralInteraction(ON_ADD_ALL);
            onCloseRecentAttachmentPills(props.composeId);
        };

        return (
            <DefaultButton
                onClick={onAddAllClicked}
                title={loc(addAllRecentAttachmentsText)}
                split={false}
                className={classNames(styles.attachmentPillButton)}>
                <Icon className={styles.AddIcon} iconName={ControlIcons.Add} />
                {loc(addAllRecentAttachmentsText)}
            </DefaultButton>
        );
    }
    return <></>;
}

function getDisplayProperties(
    props: RecentAttachmentPillContainerProps,
    pillsViewState: RecentAttachmentsPillsData,
    logGeneralInteraction: (source: string) => void,
    onChevronClicked: () => void
) {
    const pills = getAllPills(props, pillsViewState, logGeneralInteraction);
    let pillContainerStyle: string = '';
    let chevronToShow = ControlIcons.DoubleChevronDown;
    let buttonStyle: string = '';
    let buttonProps: IButtonProps = {};
    let mainContainerStyle: string;

    if (pillsViewState.showExpandButton) {
        if (pillsViewState.isExpanded) {
            chevronToShow = ControlIcons.DoubleChevronUp;
        } else {
            pillContainerStyle = styles.pillContainerCollapse;
        }
        buttonProps = {
            onClick: onChevronClicked,
            iconProps: {
                iconName: chevronToShow,
            },
        };
        buttonStyle = classNames(styles.recentAttachmentIcon, styles.expandIcon);
        mainContainerStyle = styles.mainContainerExpand;
    } else {
        buttonProps = {
            disabled: true,
        };
        buttonStyle = classNames(styles.expandIconHidden);
        mainContainerStyle = '';
    }

    return { pills, pillContainerStyle, buttonStyle, buttonProps, mainContainerStyle };
}

export { RecentAttachmentPillContainer };
